import { Select } from "./components";
import { useEffect, useRef, useState } from "react";
import {
  applyFormProfile,
  type Context,
  type Project,
  type SourceParameters,
  type Values,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";

type Choice = { id: string; name: string; kind: "global" | "personal" };
// The first profile pre-fills the form once per project/profile scope, so
// returning to the form never overwrites what someone has since typed.
const prefilled = new Set<string>();

/**
 * Switches between the profiles mapped to one form instance: authorized
 * global profiles and the profiles saved to your account. Selecting one loads
 * its current values straight into the form.
 */
export function ProfileSwitcher({
  project,
  instanceId,
  values,
  onValues,
  versionId,
  partId,
  context,
  sourceParameters,
  demoProfile,
  disabled,
  prefill = true,
  instanceIds,
  fieldIds,
  label,
  selection,
  onSelection,
}: {
  project: Project;
  instanceId: string;
  values: Values;
  onValues(values: Values): void;
  versionId?: string;
  partId?: string;
  context?: Context;
  sourceParameters?: SourceParameters;
  demoProfile?: string;
  disabled?: boolean;
  /** Load the first profile automatically. */
  prefill?: boolean;
  /** Further instances of the same profile filled by this one selector. */
  instanceIds?: string[];
  /** Limit filling to these fields (a section's own variables). */
  fieldIds?: Set<string>;
  label?: string;
  /** Controlled selection ("kind:id"), retained with saved proofs. */
  selection?: string;
  onSelection?(value: string): void;
}) {
  const transport = useWorkspaceTransport();
  const instance = project.formInstances.find((row) => row.id === instanceId);
  const form = project.forms.find((row) => row.id === instance?.formId);
  const [choices, setChoices] = useState<Choice[]>(),
    [ownSelected, setOwnSelected] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const selected = selection ?? ownSelected;
  const setSelected = (value: string) => {
    setOwnSelected(value);
    onSelection?.(value);
  };
  const latest = useRef({ values, onValues, context });
  latest.current = { values, onValues, context };
  const scope = {
    ...transport.projectInput(project, versionId),
    formId: instance?.formId || "",
    instanceId,
    partId,
    sourceParameters,
    demoProfile,
  };
  const collection =
    form?.profileLibrary && (instance?.mappedOnly || instance?.profileOnly)
      ? ("library" as const)
      : ("document" as const);
  const scopeKey = JSON.stringify([
    project.id,
    versionId,
    instanceId,
    partId,
    demoProfile,
    sourceParameters,
    collection,
  ]);
  const generation = useRef(0);
  const targets = [instanceId, ...(instanceIds || []).filter((id) => id !== instanceId)];
  const apply = async (choice: Choice, signal?: AbortSignal) => {
    const request = ++generation.current;
    setBusy(true);
    setError("");
    try {
      let next: Values = latest.current.values;
      for (const target of targets) {
        const targetScope = { ...scope, instanceId: target };
        if (choice.kind === "global") {
          const result = await transport.client.applyPredefinedProfile(
            targetScope,
            choice.id,
            next,
            signal,
          );
          next = result.values;
        } else {
          // Re-read the saved profile so changes since the last selection apply.
          const listing = await transport.client.listFormProfiles(
            { ...targetScope, collection },
            signal,
          );
          const profile = listing.profiles.find((row) => row.id === choice.id);
          if (!profile) {
            if (target === instanceId)
              throw new Error("That profile is no longer available.");
            continue;
          }
          next = applyFormProfile(
            project,
            next,
            target,
            profile.values,
            partId,
            latest.current.context,
          );
        }
      }
      if (fieldIds) {
        // A section's selector changes only that section's variables.
        const limited = { ...latest.current.values };
        for (const id of fieldIds)
          if (Object.hasOwn(next, id)) limited[id] = next[id];
          else delete limited[id];
        next = limited;
      }
      if (request === generation.current && !signal?.aborted)
        latest.current.onValues(next);
    } catch (e) {
      if (request === generation.current && !signal?.aborted)
        setError((e as Error).message);
    } finally {
      if (request === generation.current) setBusy(false);
    }
  };
  useEffect(() => {
    if (!instance) return;
    const controller = new AbortController();
    setChoices(undefined);
    setOwnSelected("");
    setError("");
    void (async () => {
      const rows: Choice[] = [];
      if (form?.profileLibrary)
        try {
          const global = await transport.client.predefinedProfileChoices(
            scope,
            controller.signal,
          );
          rows.push(
            ...global.map((row) => ({ ...row, kind: "global" as const })),
          );
        } catch {
          // Global profiles are optional for this form.
        }
      try {
        const own = await transport.client.listFormProfiles(
          { ...scope, collection },
          controller.signal,
        );
        rows.push(
          ...own.profiles.map((row) => ({
            id: row.id,
            name: row.name,
            kind: "personal" as const,
          })),
        );
      } catch {
        // Saved profiles need a published version outside the demo.
      }
      if (controller.signal.aborted) return;
      setChoices(rows);
      if (prefill && rows[0] && !selection && !prefilled.has(scopeKey)) {
        prefilled.add(scopeKey);
        setSelected(`${rows[0].kind}:${rows[0].id}`);
        await apply(rows[0], controller.signal);
      }
    })();
    return () => controller.abort();
  }, [transport, scopeKey]);
  if (!instance || (choices && !choices.length)) return null;
  const global = choices?.filter((row) => row.kind === "global") || [],
    personal = choices?.filter((row) => row.kind === "personal") || [];
  return (
    <div className="profile-switcher">
      <label>
        <span>{label || `${instance.label} profile`}</span>
        <Select
          aria-label={label || `${instance.label} profile`}
          disabled={disabled || busy || !choices}
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value);
            const [kind, ...rest] = event.target.value.split(":");
            const choice = choices?.find(
              (row) => row.kind === kind && row.id === rest.join(":"),
            );
            if (choice) void apply(choice);
          }}
        >
          <option value="">
            {choices ? "Choose a profile" : "Loading profiles…"}
          </option>
          {global.length > 0 && (
            <optgroup label="Global profiles">
              {global.map((row) => (
                <option key={row.id} value={`global:${row.id}`}>
                  {row.name}
                </option>
              ))}
            </optgroup>
          )}
          {personal.length > 0 && (
            <optgroup label="Your profiles">
              {personal.map((row) => (
                <option key={row.id} value={`personal:${row.id}`}>
                  {row.name}
                </option>
              ))}
            </optgroup>
          )}
        </Select>
      </label>
      {busy && <small role="status">Loading profile…</small>}
      {error && (
        <small role="alert" className="error-text">
          {error}
        </small>
      )}
    </div>
  );
}
